import ConfirmModal from '@/components/ConfirmModal';
import { useAppDispatch, useAppSelector } from '@/config/hooks';
import { updateDesks } from '@/services/desk.service';
import { addZone } from '@/services/zone.service';
import { format, subDays } from 'date-fns';
import { title } from 'process';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import {
  Button,
  DateRangePicker,
  Drawer,
  Form,
  InputPicker,
  Message,
  Radio,
  RadioGroup,
  Schema,
  SelectPicker,
  Stack,
  TagPicker,
  Toggle,
  useToaster
} from 'rsuite';

const { ArrayType, StringType } = Schema.Types;
const model = Schema.Model({
  desks: ArrayType().isRequired('This field is required.')
  // zone: StringType().isRequired('This field is required.')
});
const UpdateDrawer = ({ open, onClose, selectedDesks, setSelectedDesks, floor }) => {
  const dispatch = useAppDispatch();
  const [formValue, setFormValue] = useState({ desks: selectedDesks });
  const [confirmModal, setConfirmModel] = useState<any>({ open: false });
  const toaster = useToaster();
  const desks = useAppSelector((state: any) => state.desk.data).map(item => ({
    ...item,
    value: item._id,
    label: item.name
  }));

  const zones = useAppSelector((state: any) => state.zone.data).map(item => ({
    ...item,
    value: item._id,
    label: item.name
  }));

  const handleClose = () => {
    setFormValue({});
    onClose();
  };

  const handleSubmit = async () => {
    let hasErrors = false;

    const errors = model.check(formValue);

    Object.entries(errors).forEach(([field, error]) => {
      if (error.hasError) {
        hasErrors = true;
        return;
      }
    });

    if (hasErrors) {
      toaster.push(
        <Message type="error" duration={5000} showIcon>
          {`Fill required fields`}
        </Message>,
        { placement: 'topCenter' }
      );
      return;
    }
    dispatch(updateDesks(formValue, floor._id))
      .then(() => {
        toaster.push(
          <Message type="success" duration={5000} showIcon>
            {`Updated successfully`}
          </Message>,
          { placement: 'topCenter' }
        );
        setSelectedDesks([]);
        onClose();
      })
      .catch(e => {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {e}
          </Message>,
          { placement: 'topCenter' }
        );
      });
  };

  const handleAddZone = async data => {
    dispatch(addZone(data))
      .then(response => {
        toaster.push(
          <Message type="success" duration={5000} showIcon>
            {t('Zone added successfully')}
          </Message>,
          { placement: 'topCenter' }
        );
        setConfirmModel({ open: false });
        setFormValue(prevFormValue => ({ ...prevFormValue, zone: response.data._id }));
      })
      .catch(e => {
        toaster.push(
          <Message type="error" duration={5000} showIcon>
            {e}
          </Message>,
          { placement: 'topCenter' }
        );
      });
  };
  const { t } = useTranslation();
  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Edit resources')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Save')}
          </Button>
          <Button onClick={handleClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue} model={model}>
          <Form.Group>
            <Form.ControlLabel>{t('Resources')}</Form.ControlLabel>
            <Form.Control
              name="desks"
              accepter={TagPicker}
              data={desks}
              searchable={true}
              placeholder={t('Select resources')}
              block
              value={selectedDesks}
              onChange={setSelectedDesks}
              placement="auto"
            />
          </Form.Group>
          <Form.Group>
            <Form.ControlLabel>Zone</Form.ControlLabel>
            <Form.Control
              creatable
              name="zone"
              accepter={InputPicker}
              data={zones}
              searchable
              placeholder={t('Select zone')}
              block
              placement="auto"
              onChange={value => {
                // if (!value) {
                //   setFormValue(prevFormValue => {
                //     const { zone, ...rest } = prevFormValue;
                //     return rest;
                //   });
                // } else {
                setFormValue(prevFormValue => ({
                  ...prevFormValue,
                  zone: value
                }));
                // }
              }}
              value={formValue.zone || null}
              onCreate={value => {
                setConfirmModel({
                  open: true,
                  title: t('Are you sure to create a new zone ?'),
                  subtitle: '',
                  onConfirm: () =>
                    handleAddZone({
                      name: value,
                      floor: floor
                    })
                });
              }}
              onClean={() =>
                setFormValue(prevFormValue => {
                  const { zone, ...rest } = prevFormValue;
                  return rest;
                })
              }
            />
          </Form.Group>
          <Stack justifyContent="space-between">
            <Form.Group>
              <Form.ControlLabel>{t('Status')}</Form.ControlLabel>
              {/* <Form.Control
              name="status"
              accepter={Toggle}
              size="lg"
              defaultChecked
              checkedChildren="Available"
              unCheckedChildren="Blocked"
              checked={formValue?.status == 'AVAILABLE' ? true : false}
              onChange={checked => {
                const newData = { status: checked ? 'AVAILABLE' : 'BLOCK' };
                if (checked) {
                  newData.startBlock = null;
                  newData.endBlock = null;
                }
                setFormValue(prevFormValue => ({
                  ...prevFormValue,
                  ...newData
                }));
              }}
            /> */}

              <Form.Control
                name="isBlocked"
                accepter={SelectPicker}
                data={[
                  { label: 'Unblock', value: false },
                  { label: 'Block', value: true }
                ]}
                style={{ width: 220 }}
                placeholder={t('Select status')}
                placement="auto"
              />
            </Form.Group>

            <Form.Group>
              <RadioGroup
                name="radio-group-inline"
                inline
                onChange={val => {
                  const newData = { actionType: val };
                  if (val == 'forever') {
                    newData.startDate = null;
                    newData.endDate = null;
                  }
                  setFormValue(prevFormValue => ({
                    ...prevFormValue,
                    ...newData
                  }));
                }}
              >
                <Radio value="forever">{t('Forever')}</Radio>
                <Radio value="period">{t('For a Period')}</Radio>
              </RadioGroup>
              {/* <Form.Control
              name="actionType"
              accepter={SelectPicker}
              data={[
                { label: 'Forever', value: 'forever' },
                { label: 'For a Period', value: 'period' }
              ]}
              style={{ width: 220 }}
              placeholder="Select type"
              placement="auto"
              onChange={val => {
                const newData = { actionType: val };
                if (val == 'forever') {
                  newData.startDate = null;
                  newData.endDate = null;
                }
                setFormValue(prevFormValue => ({
                  ...prevFormValue,
                  ...newData
                }));
              }}
            /> */}
            </Form.Group>
          </Stack>
          {formValue?.actionType == 'period' && (
            <Form.Group style={{ marginTop: 10 }}>
              <Form.ControlLabel>
                Period of {formValue?.isBlocked ? 'block' : 'unblock'}
              </Form.ControlLabel>
              <Form.Control
                name=""
                accepter={DateRangePicker}
                size="lg"
                onChange={val => {
                  setFormValue(prevFormValue => ({
                    ...prevFormValue,
                    startDate: val ? format(val[0], 'yyyy-MM-dd') : null,
                    endDate: val ? format(val[1], 'yyyy-MM-dd') : null
                  }));
                }}
                placement="auto"
                disabledDate={(date: Date) =>
                  formValue?.isBlocked ? date < new Date() : date < subDays(new Date(), 1)
                }
              />
            </Form.Group>
          )}
        </Form>
      </Drawer.Body>

      {confirmModal.open && (
        <ConfirmModal confirmDialog={confirmModal} setConfirmDialog={setConfirmModel} />
      )}
    </Drawer>
  );
};

export default UpdateDrawer;
